import React, { useState, useMemo, useEffect } from "react";
import { Form, Button, Input, Flex, Divider, Radio, Tooltip, Card } from "antd";

import useUpdate from "hooks/useUpdate";
import useFormErrors from "hooks/useFormErrors";
import { useIssueKinds } from "context/providers/issue_kinds";
import IconUpload from "./components/IconUpload";
import issue_kind_icons from "helpers/issue_kind_icons";
import { useParams } from "react-router-dom";

const issue_kind_icon_type_options = [
  { label: "Lista ikon", value: "fa_icon" },
  { label: "Własna ikona", value: "upload" },
];

const visible_count_limit = 80;

const moveSelectedToTop = (icons, selected) => {
  if (!selected) return icons;

  const index = icons.findIndex(({ name }) => name === selected);
  if (index === -1) return icons;

  return [icons[index], ...icons.slice(0, index), ...icons.slice(index + 1)];
};

const Icon = () => {
  const { id } = useParams();
  const { data, refetch } = useIssueKinds();
  const issue_kind = data.find((item) => item.id === id);
  const [search, setSearch] = useState("");

  const initial_values = useMemo(() => {
    if (!issue_kind) return {};

    return {
      icon_type: issue_kind?.icon_type,
      fa_icon_name: issue_kind?.fa_icon_name,
    };
  }, [issue_kind]);

  const [visible_count, setVisibleCount] = useState(visible_count_limit);

  const [form] = Form.useForm();

  const { pristine, has_error, onFieldsChange, reset } =
    useFormErrors(initial_values);

  const { is_saving, updateAsync } = useUpdate({
    path: `/issue_kinds/${id}`,
    form,
    onSuccess: refetch,
  });

  const handleClose = () => {
    reset();
    setVisibleCount(visible_count_limit);
    setSearch("");
  };

  const selected_icon = useMemo(
    () => issue_kind_icons.find((i) => i.name === initial_values?.fa_icon_name),
    [initial_values]
  );

  const sorted_icons = useMemo(
    () =>
      moveSelectedToTop(issue_kind_icons, initial_values?.fa_icon_name).filter(
        ({ real_name }) => real_name.toLowerCase().includes(search)
      ),
    [initial_values?.fa_icon_name, search]
  );

  const visible_icons = useMemo(
    () => sorted_icons.slice(0, visible_count),
    [sorted_icons, visible_count]
  );

  const other_icons = useMemo(
    () => visible_icons.filter((i) => i.name !== initial_values?.fa_icon_name),
    [visible_icons, initial_values]
  );

  useEffect(() => {
    setVisibleCount(visible_count_limit);
  }, [search]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFieldsChange={onFieldsChange}
      initialValues={initial_values}
      onFinish={async (values) => {
        try {
          await updateAsync(values);
          handleClose();
        } catch (error) {
          console.log(error);
        }
      }}
    >
      <Card
        title="Ikona"
        extra={
          <Button
            type="primary"
            htmlType="submit"
            disabled={pristine || has_error}
            loading={is_saving}
          >
            Zapisz
          </Button>
        }
      >
        <div style={{ maxWidth: 300 }}>
          <Form.Item name="icon_type" label="Typ ikony">
            <Radio.Group
              block
              options={issue_kind_icon_type_options}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>
        </div>
        {(form.getFieldValue("icon_type") || initial_values.icon_type) ===
          "upload" ? (
          <IconUpload
            icon_url={issue_kind?.icon_url}
            upload={updateAsync}
          />
        ) : (
          <>
            <Divider orientation="left" size="medium">
              Aktualna ikona
            </Divider>
            <Tooltip placement="top" title={selected_icon?.real_name}>
              {selected_icon?.icon()}
            </Tooltip>
            <Divider orientation="left" size="medium">
              Pozostałe ikony
            </Divider>
            <div style={{ maxWidth: 300 }}>
              <Input
                placeholder="Szukaj ikonki"
                allowClear
                size="medium"
                value={search}
                onChange={({ target }) => setSearch(target.value)}
              />
            </div>
            <Divider orientation="left" size="medium" />
            <div className="fa-icons-container">
              <Form.Item name="fa_icon_name">
                <Radio.Group
                  className="fa-icons-radio"
                  options={[selected_icon, ...other_icons]
                    .filter(Boolean)
                    .map(({ name, real_name, icon }) => ({
                      value: name,
                      label: (
                        <Tooltip placement="top" title={real_name}>
                          {icon()}
                        </Tooltip>
                      ),
                    }))}
                />
              </Form.Item>
              {visible_count < sorted_icons.length && (
                <Flex justify="center" style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    htmlType="button"
                    onClick={() => setVisibleCount((c) => c + 40)}
                  >
                    Pokaż więcej
                  </Button>
                </Flex>
              )}
            </div>
          </>
        )}
      </Card>
    </Form>
  );
};

export default Icon;
